import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Order } from "@/store/orderStore";
import { supabase } from "@/integrations/supabase/client";
import { submitOrder } from "@/services/orders";
import { useTranslation } from "react-i18next";
import { CartItem } from "@/types/orders";
import { deductInventory } from "@/services/inventory/inventoryService";
import { useOrderSecurity } from "@/hooks/useOrderSecurity";
import { useFeatureFlagStore } from "@/store/featureFlagStore";
import { initiatePayment } from "@/services/payments/paymentService";
import { useLocation } from "react-router-dom";

interface OrderSubmissionProps {
  storeId: string;
  tableId: string;
  tableName: string;
  tableNumber: number;
  specialInstructions: string;
  cartItems: CartItem[];
  selectedTip: number;
  onOrderPlaced?: (order: Order) => void;
  clearCart: () => void;
  calculateTotal: () => number;
  calculateSubtotal: () => number;
}

// Get client IP for tracking
async function getClientIP(): Promise<string> {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn("Could not get client IP:", error);
    return "unknown";
  }
}

export function useOrderSubmission({
  storeId,
  tableId,
  tableName,
  tableNumber,
  specialInstructions,
  cartItems,
  selectedTip,
  onOrderPlaced,
  clearCart,
  calculateTotal,
  calculateSubtotal,
}: OrderSubmissionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { isSecurityChecking, performOrderSecurity } = useOrderSecurity();
  const location = useLocation();

  // Modify proceedWithOrder to remove duplicate security check
  const proceedWithOrder = async (isPaid: boolean, checkoutUrl?: string) => {
    // Remove the security check from here since it's now done at the beginning

    // Get client IP for tracking
    const clientIP = await getClientIP();

    try {
      // Generate a new order ID
      const orderId = crypto.randomUUID();

      // Create the order items
      const orderItems = cartItems.map((item) => ({
        id: crypto.randomUUID(),
        order_id: orderId,
        menuItemId: item.menuItemId,
        menuItemName: item.menuItemName,
        quantity: item.quantity,
        price: item.price,
        image_url: item.imageUrl,
      }));

      // Calculate tip amount
      const subtotal = calculateSubtotal();
      const tipAmount = (subtotal * selectedTip) / 100;

      // Submit the order
      const result = await submitOrder(
        {
          id: orderId,
          storeId: storeId,
          tableId: tableId,
          tableNumber: tableNumber,
          tableName: tableName,
          status: "pending",
          totalAmount: calculateTotal(),
          specialInstructions: specialInstructions.trim() || undefined,
          payment_method: "Cash",
          is_payed: false,
          tip_amount: tipAmount,
          tip_percentage: selectedTip,
        },
        orderItems
      );

      if (!result) {
        throw new Error("Failed to create order");
      }

      // Show success message
      setIsSuccessDialogOpen(true);

      // Notify parent component
      if (onOrderPlaced) {
        onOrderPlaced(result);
      }

      // Clear the cart
      clearCart();

      return result;
    } catch (error) {
      console.error("Error submitting order:", error);
      toast({
        title: t("error"),
        description: error instanceof Error ? error.message : t("orderError"),
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitOrder = async (paymentMethod: "Cash" | "Digital") => {
    if (cartItems.length === 0) {
      toast({
        title: t("cartEmptyToastTitle"),
        description: t("cartEmptyToastDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Perform security check first
      const securityPassed = await performOrderSecurity(tableId, storeId);

      if (!securityPassed) {
        toast({
          title: t("error"),
          description: t("securityCheckFailed"),
          variant: "destructive",
        });
        return null;
      }

      // Generate a new order ID that will be used for both payment and order creation
      const orderId = crypto.randomUUID();

      // Handle different payment methods
      if (paymentMethod === "Digital") {
        // Store cart data in localStorage for order creation after payment
        const pendingOrderData = {
          orderId,
          storeId,
          tableId,
          tableName,
          tableNumber,
          specialInstructions,
          cartItems,
          paymentMethod,
          totalAmount: calculateTotal(),
          timestamp: new Date().toISOString(), // Add timestamp for debugging
          is_payed: false, // Initially false for digital payments
        };

        try {
          localStorage.setItem(
            "pendingOrder",
            JSON.stringify(pendingOrderData)
          );
        } catch (storageError) {
          console.error("Failed to store pending order data:", storageError);
          toast({
            title: t("error"),
            description: t("paymentPreparationFailed"),
            variant: "destructive",
          });
          return null;
        }

        // Initialize payment flow
        const paymentResult = await initiatePayment(
          orderId,
          storeId,
          calculateTotal(),
          "customer@example.com", // TODO: Get customer email
          tableId
        );

        if (!paymentResult.success) {
          toast({
            title: t("error"),
            description: paymentResult.error || t("paymentFailed"),
            variant: "destructive",
          });
          return null;
        }

        // Redirect to Paystack checkout
        if (paymentResult.checkoutUrl) {
          window.location.href = paymentResult.checkoutUrl;
        }

        return null; // Don't create order for digital payments - it will be created after payment
      }

      // For cash payments only, proceed with order creation
      return await proceedWithOrder(true); // Always true since we handle digital payments separately
    } catch (error) {
      console.error("Error submitting order:", error);
      toast({
        title: t("error"),
        description: error instanceof Error ? error.message : t("orderError"),
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting: isSubmitting || isSecurityChecking,
    isSuccessDialogOpen,
    setIsSuccessDialogOpen,
    handleSubmitOrder,
  };
}

// Enhanced submit order function with security tracking
async function submitOrderWithSecurity(
  orderData: {
    id: string;
    storeId: string;
    tableId: string;
    tableNumber: number;
    tableName: string;
    status: "pending";
    totalAmount: number;
    specialInstructions?: string;
    clientIP: string;
    is_payed: boolean;
  },
  orderItems: {
    id: string;
    order_id: string;
    menuItemId: string;
    menuItemName: string;
    quantity: number;
    price: number;
    image_url: string;
  }[]
): Promise<Order | null> {
  try {
    // Insert the order with client IP tracking
    const { data, error: orderError } = await supabase
      .from("orders")
      .insert({
        id: orderData.id,
        store_id: orderData.storeId,
        table_id: orderData.tableId,
        table_number: orderData.tableNumber,
        table_name: orderData.tableName,
        status: orderData.status,
        total_amount: orderData.totalAmount,
        special_instructions: orderData.specialInstructions,
        client_ip: orderData.clientIP, // Track client IP
        is_payed: orderData.is_payed,
      })
      .select()
      .single();

    if (orderError) {
      console.error("Error submitting order:", orderError);
      return null;
    }

    const order = data;

    // Insert order items
    if (orderItems.length > 0) {
      const formattedItems = orderItems.map((item) => ({
        id: item.id,
        order_id: item.order_id,
        menu_item_id: item.menuItemId,
        menu_item_name: item.menuItemName,
        quantity: item.quantity,
        price: item.price,
        image_url: item.image_url,
      }));

      // Try using the function first (most reliable)
      try {
        const { error: fnError } = await supabase.rpc(
          "insert_public_order_items",
          {
            items: formattedItems,
          }
        );

        if (fnError) {
          console.error("Error using RPC to insert items:", fnError);
          throw fnError;
        }
      } catch (rpcError) {
        console.warn("Falling back to direct insert:", rpcError);

        // Fallback to direct insert if RPC fails
        const { error: directError } = await supabase
          .from("order_items")
          .insert(formattedItems);

        if (directError) {
          console.error(
            "Error with direct insert of order items:",
            directError
          );
        }
      }
    }

    // Return formatted order
    if (order) {
      return {
        id: order.id,
        store_id: order.store_id,
        table_id: order.table_id,
        tableNumber: order.table_number,
        tableName: order.table_name,
        status: order.status as "pending",
        totalAmount: order.total_amount,
        items: orderItems.map((item) => ({
          id: item.id,
          order_id: item.order_id,
          menu_item_id: item.menuItemId,
          menu_item_name: item.menuItemName,
          price: item.price,
          quantity: item.quantity,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        specialInstructions: order.special_instructions,
        createdAt: order.created_at,
        updatedAt: order.updated_at,
        is_payed: order.is_payed,
      };
    }

    return null;
  } catch (error) {
    console.error("Error in submitOrderWithSecurity:", error);
    return null;
  }
}
